/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Search Engine Module
 * Core search functionality and algorithms
 */

import { fuzzySearch } from './fuzzy-search.js';
import { searchPatterns } from './regex-patterns.js';
import { filterManager } from '../filters/filter-manager.js';
import { localStorageManager } from '../storage/local-storage.js';

class SearchEngine {
    constructor() {
        this.searchIndex = null;
        this.lastQuery = '';
        this.searchHistory = [];
        this.maxHistorySize = 50;
        this.searchCallbacks = [];
        
        // Performance optimization
        this.searchDebounceTimer = null;
        this.searchDebounceDelay = 150; // ms
    }
    
    /**
     * Get normalized file name (supports legacy fields)
     */
    getFileName(file) {
        return (file && (file.name || file.n)) || '';
    }
    
    /**
     * Get normalized file path (supports legacy fields)
     */
    getFilePath(file) {
        return (file && (file.path || file.p)) || '';
    }
    
    /**
     * Generate random seed on page load
     * Seed changes on every page load for different file order
     * Uses script execution time to detect new page load (more reliable than navigationStart)
     */
    getRandomSeed(forceNew = false) {
        let seed;
        try {
            // Check if seed was already generated for this page load
            // We use a flag that's set when seed is first generated
            const seedGenerated = sessionStorage.getItem('random_files_seed_generated');
            seed = sessionStorage.getItem('random_files_seed');
            
            // If no seed exists or flag is not set, this is a new page load
            // OR if forceNew is true (for empty query without filters)
            if (!seed || !seedGenerated || forceNew) {
                // New page load or forced new seed - generate new seed
                seed = Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
                sessionStorage.setItem('random_files_seed', seed);
                sessionStorage.setItem('random_files_seed_generated', 'true');
            }
        } catch (e) {
            seed = Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
        }
        
        return seed;
    }
    
    /**
     * Get seed for empty query - generates new seed if no filters are active
     * This ensures shuffle happens when user clears search and returns to empty state
     */
    getSeedForEmptyQuery(hasActiveFilters = false) {
        // If there are active filters, use existing seed (for pagination consistency)
        if (hasActiveFilters) {
            return this.getRandomSeed(false);
        }
        // If no filters, generate new seed to trigger shuffle
        return this.getRandomSeed(true);
    }
    
    /**
     * Reset seed generation flag (called on page unload to prepare for next page load)
     * This ensures new seed is generated on next page load
     */
    resetSeedForNextPageLoad() {
        try {
            sessionStorage.removeItem('random_files_seed_generated');
        } catch (e) {
            // Ignore errors
        }
    }
    
    /**
     * Simple hash function for deterministic file selection
     */
    simpleHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash;
    }
    
    /**
     * Select random files in groups of 3 consecutive files
     * Algorithm: group files by 3, assign hash-based priority to each group, then select groups by priority
     * This creates deterministic "random" order where files appear in groups of 3 consecutive items
     */
    selectRandomFiles(files, seed) {
        if (!files || files.length === 0) return files;
        
        const GROUP_SIZE = 3;
        
        // If we have less than GROUP_SIZE files, return as is
        if (files.length < GROUP_SIZE) {
            return files;
        }
        
        // Create array of group start indices: [0, 3, 6, 9, ...] up to files.length - GROUP_SIZE
        const groupStarts = [];
        for (let i = 0; i <= files.length - GROUP_SIZE; i += GROUP_SIZE) {
            groupStarts.push(i);
        }
        
        // Assign random priority to each group based on hash(startIndex + seed)
        // This creates a deterministic "random" order for groups
        const groupsWithPriority = groupStarts.map((startIndex) => {
            const hash = this.simpleHash(startIndex.toString() + seed);
            return { startIndex, priority: hash };
        });
        
        // Sort groups by priority - this gives random order for groups
        groupsWithPriority.sort((a, b) => a.priority - b.priority);
        
        // Build result: for each group, take GROUP_SIZE consecutive files
        const result = [];
        groupsWithPriority.forEach((group) => {
            for (let i = 0; i < GROUP_SIZE && (group.startIndex + i) < files.length; i++) {
                result.push(files[group.startIndex + i]);
            }
        });
        
        // If there are remaining files that don't fit into complete groups, add them at the end
        const processedCount = result.length;
        if (processedCount < files.length) {
            for (let i = processedCount; i < files.length; i++) {
                result.push(files[i]);
            }
        }
        
        return result;
    }
    
    /**
     * Initialize search engine
     */
    init(options = {}) {
        this.searchDebounceDelay = options.debounceDelay || 150;
        this.maxHistorySize = options.maxHistorySize || 50;
        
        // Reset seed generation flag on init to ensure new seed on page load
        // This flag is set when seed is generated, and cleared here to allow new generation
        try {
            sessionStorage.removeItem('random_files_seed_generated');
        } catch (e) {
            // Ignore errors
        }
        
        // Build initial index if data provided
        if (options.initialData) {
            this.buildIndex(options.initialData);
        }
    }
    
    /**
     * Build search index from data
     */
    buildIndex(data) {
        
        this.searchIndex = {
            files: data,
            byName: new Map(),
            byPath: new Map(),
            byCategory: new Map(),
            byFormat: new Map(),
            tokens: new Map()
        };
        
        // Index files by various properties
        data.forEach((file, index) => {
            // Index by name (supports legacy 'n')
            const name = this.getFileName(file);
            if (name) {
                const nameLower = name.toLowerCase();
                if (!this.searchIndex.byName.has(nameLower)) {
                    this.searchIndex.byName.set(nameLower, []);
                }
                this.searchIndex.byName.get(nameLower).push(index);
                
                // Tokenize name for partial matching
                this.tokenizeAndIndex(name, index);
            }
            
            // Index by path (supports legacy 'p')
            const path = this.getFilePath(file);
            if (path) {
                const pathLower = path.toLowerCase();
                if (!this.searchIndex.byPath.has(pathLower)) {
                    this.searchIndex.byPath.set(pathLower, []);
                }
                this.searchIndex.byPath.get(pathLower).push(index);
            }
            
            // Index by category
            if (file.category) {
                if (!this.searchIndex.byCategory.has(file.category)) {
                    this.searchIndex.byCategory.set(file.category, []);
                }
                this.searchIndex.byCategory.get(file.category).push(index);
            }
            
            // Index by format
            if (file.format) {
                if (!this.searchIndex.byFormat.has(file.format)) {
                    this.searchIndex.byFormat.set(file.format, []);
                }
                this.searchIndex.byFormat.get(file.format).push(index);
            }
        });
        
    }
    
    /**
     * Tokenize text and add to token index
     */
    tokenizeAndIndex(text, fileIndex) {
        const tokens = this.tokenize(text);
        tokens.forEach(token => {
            if (!this.searchIndex.tokens.has(token)) {
                this.searchIndex.tokens.set(token, new Set());
            }
            this.searchIndex.tokens.get(token).add(fileIndex);
        });
    }
    
    /**
     * Tokenize text into searchable parts
     */
    tokenize(text) {
        return text
            .toLowerCase()
            .split(/[\s\-_\.\/\\]+/)
            .filter(token => token.length > 0);
    }
    /**
     * Perform search with query
     */
    search(query, options = {}) {
        if (debugMode === '1') {
            console.log('[SearchEngine] search() called:', { query, options, hasIndex: !!this.searchIndex });
        }
        
        if (!this.searchIndex) {
            if (debugMode === '1') {
                console.warn('Search index not built');
            }
            return [];
        }
        
        // Debounce search if enabled
        if (options.debounce && this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        
        const performSearch = () => {
            const results = this._performSearch(query, options);
            
            // Save to history
            if (query && query !== this.lastQuery) {
                this.addToHistory(query);
                this.lastQuery = query;
            }
            
            // Notify callbacks
            this.notifySearchComplete(results, query);
            
            return results;
        };
        
        if (options.debounce) {
            return new Promise((resolve) => {
                this.searchDebounceTimer = setTimeout(() => {
                    resolve(performSearch());
                }, this.searchDebounceDelay);
            });
        }
        
        return performSearch();
    }
    
    /**
     * Internal search implementation
     */
    _performSearch(query, options) {
        const queryLower = query ? query.toLowerCase().trim() : '';
        if (debugMode === '1') {
            console.log('[SearchEngine] _performSearch start:', { query, queryLower, isEmpty: !queryLower, options });
        }
        
        if (!queryLower) {
            if (debugMode === '1') {
                console.log('[SearchEngine] Empty query detected, applying shuffle...');
            }
            // Empty query - apply filters first, then shuffle with seed
            let allFiles = this.searchIndex.files;
            if (debugMode === '1') {
                console.log('[SearchEngine] Total files in index:', allFiles.length);
            }
            
            // Apply filters BEFORE shuffle
            if (options.applyFilters !== false && window.filterManager) {
                const before = allFiles.length;
                allFiles = allFiles.filter(file => window.filterManager.matchesFilters(file));
                if (debugMode === '1') {
                    console.log('[SearchEngine] filters applied (empty query):', { before, after: allFiles.length });
                }
            } else if (debugMode === '1') {
                console.log('[SearchEngine] Filters NOT applied:', { applyFilters: options.applyFilters, hasFilterManager: !!window.filterManager });
            }
            
            // Select random files using seed-based algorithm
            // Check if there are active filters - if no filters, generate new seed for shuffle
            let hasActiveCategoryFilters = false;
            if (window.filterController && typeof window.filterController.getSelectedCategories === 'function') {
                const selections = window.filterController.getSelectedCategories() || {};
                // Check if any category filter is active
                hasActiveCategoryFilters = Object.keys(selections).some(key => {
                    const values = selections[key];
                    return Array.isArray(values) && values.length > 0;
                });
            }
            
            const hasActiveFilters = hasActiveCategoryFilters || 
                (options.dateFrom || options.dateTo) ||
                (this.activeFilters && (this.activeFilters.publicOnly || this.activeFilters.dateFrom || this.activeFilters.dateTo));
            
            if (debugMode === '1') {
                console.log('[SearchEngine] Getting random seed for empty query...', { hasActiveFilters, hasActiveCategoryFilters });
            }
            // Generate new seed if no filters are active (to trigger shuffle on search clear)
            const seed = this.getSeedForEmptyQuery(hasActiveFilters);
            if (debugMode === '1') {
                console.log('[SearchEngine] Seed:', seed);
                console.log('[SearchEngine] Selecting random files from', allFiles.length, 'files...');
            }
            const randomFiles = this.selectRandomFiles(allFiles, seed);
            if (debugMode === '1') {
                console.log('[SearchEngine] Random files selected (empty query):', { seed, count: randomFiles.length, firstFew: randomFiles.slice(0, 3).map(f => (f.n || f.name || '')) });
            }
            return randomFiles;
        }
        
        let results = [];
        
        // Check for special search patterns
        if (this.isRegexSearch(query)) {
            results = this.regexSearch(query);
        } else if (this.isWildcardSearch(query)) {
            results = this.wildcardSearch(query);
        } else if (options.fuzzy) {
            results = this.fuzzySearchFiles(query, options.fuzzyThreshold);
        } else {
            results = this.standardSearch(queryLower);
        }
        
        // Apply filters if filter manager is active
        if (options.applyFilters !== false && window.filterManager) {
            const before = results.length;
            results = results.filter(file => window.filterManager.matchesFilters(file));
            if (debugMode === '1') {
                console.log('[SearchEngine] filters applied:', { before, after: results.length });
            }
        }
        
        // NOTE: No global fuzzy fallback here by design. We only rerank the already found list.
        
        // Sort results by relevance (rerank only, do not drop any items)
        if (options.sort !== false) {
            if (debugMode === '1') {
                console.log('[SearchEngine] sortByRelevance start:', { size: results.length });
            }
            const __t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
            results = this.sortByRelevance(results, queryLower, options);
            const __t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
            if (debugMode === '1') {
                console.log('[SearchEngine] sortByRelevance done:', { size: results.length, ms: Number((__t1 - __t0).toFixed(2)) });
            }
        }
        
        // Limit results if specified
        if (options.limit && options.limit > 0) {
            results = results.slice(0, options.limit);
        }
        
        return results;
    }
    
    /**
     * Add query to search history
     */
    addToHistory(query) {
        if (!query || query.trim() === '') return;
        
        // Remove duplicate if exists
        const index = this.searchHistory.indexOf(query);
        if (index > -1) {
            this.searchHistory.splice(index, 1);
        }
        
        // Add to beginning
        this.searchHistory.unshift(query);
        
        // Limit history size
        if (this.searchHistory.length > this.maxHistorySize) {
            this.searchHistory = this.searchHistory.slice(0, this.maxHistorySize);
        }
    }
    
    /**
     * Notify all registered callbacks about search completion
     */
    notifySearchComplete(results, query) {
        this.searchCallbacks.forEach(callback => {
            try {
                callback(results, query);
            } catch (error) {
                if (debugMode === '1') {
                    console.error('[SearchEngine] Error in search callback:', error);
                }
            }
        });
    }
}

// Create and export singleton instance
const searchEngine = new SearchEngine();
export { SearchEngine, searchEngine };
